/**
 * Modale création dossier Employé RH — alignée UX sur AgentTerrainCreateModal.
 *
 * TODO (architecture identité) : à terme, une entité Person centralise nom/prénom/téléphone/email ;
 * TerrainProfile (agent) et RHProfile (EmployeRH) en sont des projections — éviter la duplication
 * métier au-delà du préremplissage / mirroring contrôlé (voir flux Person → TerrainProfile → RHProfile → UserAccount).
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, X, HardHat, ExternalLink } from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../../lib/axios";

const STATUTS = [
  "ACTIF",
  "EN_CONGE",
  "SUSPENDU",
  "FIN_CONTRAT",
  "ARCHIVE",
] as const;

type TabId = "identite" | "contrat" | "paie" | "documents" | "liaison";

const TABS: { id: TabId; label: string }[] = [
  { id: "identite", label: "Identité" },
  { id: "contrat", label: "Contrat RH" },
  { id: "paie", label: "Paie" },
  { id: "documents", label: "Documents" },
  { id: "liaison", label: "Liaison terrain" },
];

type TerrainAgentPayload = {
  id: number;
  nom?: string | null;
  prenom?: string | null;
  telephone1?: string | null;
  fonction?: string | null;
  typeAgent?: string | null;
  statutOperationnel?: string | null;
  equipeId?: number | null;
  equipe?: { nom: string; zone?: { nom?: string | null } | null } | null;
  zonePreference?: { nom?: string | null } | null;
};

const inputClass =
  "mt-1 w-full rounded-xl border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-sm text-white outline-none focus:border-violet-500/50";

function badgeClass(active: boolean): string {
  return active
    ? "border-violet-500/60 bg-violet-950/50 text-violet-100"
    : "border-slate-600 bg-slate-900/80 text-slate-400 hover:border-slate-500";
}

function formatStatutOp(raw?: string | null): string {
  const u = String(raw ?? "").toUpperCase();
  const map: Record<string, string> = {
    DISPONIBLE: "Disponible",
    AFFECTE: "Affecté",
    EN_TOURNEE: "En tournée",
    INDISPONIBLE: "Indisponible",
    SUSPENDU: "Suspendu",
    ARCHIVE: "Archivé",
  };
  return map[u] ?? (raw ? String(raw) : "—");
}

function typeAgentLabel(type?: string | null): string {
  const t = String(type ?? "").toUpperCase();
  if (t === "JOURNALIER") return "Journalier";
  if (t === "PERMANENT_TERRAIN") return "Permanent terrain";
  return type ?? "—";
}

type Props = {
  open: boolean;
  onClose: () => void;
};

export default function EmployeRHCreateModal({ open, onClose }: Props): React.JSX.Element | null {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const agentPref = searchParams.get("agentId");

  const [loading, setLoading] = useState(false);
  const [boot, setBoot] = useState(false);
  const [terrainAgent, setTerrainAgent] = useState<TerrainAgentPayload | null>(null);

  const [activeTab, setActiveTab] = useState<TabId>("identite");

  const [matriculeRH, setMatriculeRH] = useState("");
  const [statutRH, setStatutRH] = useState<string>("ACTIF");
  const [typeContrat, setTypeContrat] = useState("");
  const [posteRH, setPosteRH] = useState("");
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [telephone, setTelephone] = useState("");
  const [email, setEmail] = useState("");
  const [salaireMensuel, setSalaireMensuel] = useState("");
  const [modePaiement, setModePaiement] = useState("");
  const [comptePaiement, setComptePaiement] = useState("");
  const [dateEmbauche, setDateEmbauche] = useState("");
  const [dateFinContrat, setDateFinContrat] = useState("");

  const resetForm = useCallback(() => {
    setMatriculeRH("");
    setStatutRH("ACTIF");
    setTypeContrat("");
    setPosteRH("");
    setNom("");
    setPrenom("");
    setTelephone("");
    setEmail("");
    setSalaireMensuel("");
    setModePaiement("");
    setComptePaiement("");
    setDateEmbauche("");
    setDateFinContrat("");
    setTerrainAgent(null);
    setActiveTab("identite");
  }, []);

  useEffect(() => {
    if (!open) {
      resetForm();
      setBoot(false);
      return;
    }

    const id = agentPref ? Number(agentPref) : NaN;
    if (!agentPref || !Number.isInteger(id) || id <= 0) {
      setTerrainAgent(null);
      setBoot(false);
      return;
    }

    setBoot(true);
    void (async () => {
      try {
        const res = await api.get(`/agents/${id}`);
        const a = res.data as TerrainAgentPayload;
        setTerrainAgent(a);
        setNom((a.nom ?? "").trim());
        setPrenom((a.prenom ?? "").trim());
        setTelephone((a.telephone1 ?? "").trim());
        setPosteRH((prev) => prev.trim() || (a.fonction ?? "").trim());
      } catch {
        toast.warning("Impossible de charger l’agent terrain — création RH sans lien automatique.");
        setTerrainAgent(null);
      } finally {
        setBoot(false);
      }
    })();
  }, [open, agentPref, resetForm]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const zoneLabel = useMemo(() => {
    if (!terrainAgent) return "—";
    const zPref = terrainAgent.zonePreference?.nom?.trim();
    const zEq = terrainAgent.equipe?.zone?.nom?.trim();
    return zPref || zEq || "—";
  }, [terrainAgent]);

  const equipeLabel = useMemo(() => {
    if (!terrainAgent?.equipe?.nom) return "—";
    return terrainAgent.equipe.nom;
  }, [terrainAgent]);

  const buildBody = (): Record<string, unknown> => {
    const body: Record<string, unknown> = {
      matriculeRH: matriculeRH.trim() || undefined,
      statutRH,
      typeContrat: typeContrat.trim() || undefined,
      posteRH: posteRH.trim() || undefined,
      nom: nom.trim() || undefined,
      prenom: prenom.trim() || undefined,
      telephone: telephone.trim() || undefined,
      email: email.trim() || undefined,
      salaireMensuel: salaireMensuel.trim() || undefined,
      modePaiement: modePaiement.trim() || undefined,
      comptePaiement: comptePaiement.trim() || undefined,
      dateEmbauche: dateEmbauche || undefined,
      dateFinContrat: dateFinContrat || undefined,
    };
    if (agentPref && Number.isInteger(Number(agentPref)) && Number(agentPref) > 0) {
      body.agentTerrainId = Number(agentPref);
    }
    return body;
  };

  const submit = async (mode: "list" | "detail") => {
    setLoading(true);
    try {
      const res = await api.post("/rh/employes", buildBody());
      const emp = res.data?.employe as { id?: number };
      toast.success(
        agentPref ? "Dossier RH créé et lié à l’agent terrain." : "Dossier RH créé."
      );
      onClose();
      if (mode === "detail" && emp?.id != null) {
        navigate(`/admin/rh/employes/${emp.id}`);
      } else {
        navigate("/admin/rh/employes");
      }
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { error?: string; id?: number } };
      };
      const code = ax.response?.data?.error;
      if (code === "EMPLOYE_ALREADY_LINKED" && ax.response?.data?.id) {
        toast.info("Un dossier RH existe déjà pour cet agent — redirection.");
        onClose();
        navigate(`/admin/rh/employes/${ax.response.data.id}`);
      } else {
        toast.error(ax.response?.data?.error ?? "Création impossible.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const primaryCreateLabel = agentPref ? "Créer RH + lier terrain" : "Créer dossier RH";
  const secondaryCreateLabel = "Créer et ouvrir fiche RH";

  return (
    <div
      className="fixed inset-0 z-[70] flex min-w-0 items-end justify-center overflow-x-hidden bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="relative flex max-h-[90dvh] min-h-0 w-full max-w-[1200px] min-w-0 flex-col overflow-hidden rounded-t-2xl border border-slate-700/80 bg-slate-900 shadow-[0_25px_80px_-12px_rgba(0,0,0,0.85)] sm:max-h-[min(92dvh,920px)] sm:rounded-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="employe-rh-create-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="absolute right-2 top-2 z-10 inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white sm:right-4 sm:top-4"
          onClick={onClose}
          aria-label="Fermer"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>

        {/* Header : badges + titre */}
        <header className="shrink-0 border-b border-slate-800 px-5 pb-4 pt-5 pr-14 sm:px-8 sm:pt-7">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md border border-violet-500/40 bg-violet-950/40 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-200">
              RH
            </span>
            <span className="rounded-md border border-slate-600 bg-slate-900 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-300">
              DOSSIER EMPLOYÉ
            </span>
            {agentPref ? (
              <span className="rounded-md border border-teal-500/45 bg-teal-950/35 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-teal-100">
                LIÉ TERRAIN
              </span>
            ) : null}
          </div>
          <h2
            id="employe-rh-create-title"
            className="mt-4 break-words text-xl font-bold tracking-tight text-white sm:text-2xl"
          >
            Nouveau dossier RH
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-400">
            Créer un dossier administratif RH indépendant ou lié à un agent terrain.
          </p>

          {/* Carte agent terrain lié */}
          {agentPref && terrainAgent ? (
            <div className="mt-6 rounded-2xl border border-teal-500/35 bg-gradient-to-br from-teal-950/40 to-slate-950/60 p-4 sm:p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-2 text-teal-100">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-500/15 text-lg">
                    👷
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-teal-300/90">
                      Agent terrain lié
                    </p>
                    <p className="text-base font-semibold text-white">
                      {(terrainAgent.nom ?? "").trim()}{" "}
                      {(terrainAgent.prenom ?? "").trim()}
                    </p>
                  </div>
                </div>
                <Link
                  to={`/admin/agents/${terrainAgent.id}`}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-teal-600/50 px-3 py-1.5 text-xs font-semibold text-teal-100 hover:bg-teal-950/60"
                >
                  Fiche terrain <ExternalLink className="h-3.5 w-3.5 opacity-80" />
                </Link>
              </div>

              <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <dt className="text-[11px] font-medium uppercase text-slate-500">Téléphone</dt>
                  <dd className="mt-0.5 text-slate-100">
                    {terrainAgent.telephone1?.trim() || "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] font-medium uppercase text-slate-500">Fonction terrain</dt>
                  <dd className="mt-0.5 text-slate-100">{terrainAgent.fonction?.trim() || "—"}</dd>
                </div>
                <div>
                  <dt className="text-[11px] font-medium uppercase text-slate-500">Disponibilité</dt>
                  <dd className="mt-0.5 text-slate-100">
                    {formatStatutOp(terrainAgent.statutOperationnel)}
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] font-medium uppercase text-slate-500">Équipe</dt>
                  <dd className="mt-0.5 text-slate-100">{equipeLabel}</dd>
                </div>
                <div>
                  <dt className="text-[11px] font-medium uppercase text-slate-500">Zone</dt>
                  <dd className="mt-0.5 text-slate-100">{zoneLabel}</dd>
                </div>
                <div>
                  <dt className="text-[11px] font-medium uppercase text-slate-500">Type agent</dt>
                  <dd className="mt-0.5 text-slate-100">
                    {typeAgentLabel(terrainAgent.typeAgent)}
                  </dd>
                </div>
              </dl>

              <div className="mt-4 flex flex-wrap gap-2">
                {String(terrainAgent.typeAgent ?? "").toUpperCase() === "JOURNALIER" ? (
                  <span className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-semibold text-emerald-200">
                    Journalier
                  </span>
                ) : null}
                {String(terrainAgent.typeAgent ?? "").toUpperCase() === "PERMANENT_TERRAIN" ? (
                  <span className="rounded-md border border-amber-500/40 bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-100">
                    Permanent
                  </span>
                ) : null}
                {String(terrainAgent.statutOperationnel ?? "").toUpperCase() === "DISPONIBLE" ? (
                  <span className="rounded-md border border-sky-500/40 bg-sky-500/10 px-2 py-0.5 text-[11px] font-semibold text-sky-100">
                    Disponible
                  </span>
                ) : null}
                {terrainAgent.equipeId == null ? (
                  <span className="rounded-md border border-slate-600 bg-slate-800/90 px-2 py-0.5 text-[11px] font-semibold text-slate-300">
                    Sans équipe
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}
        </header>

        {/* Tabs */}
        <div className="shrink-0 border-b border-slate-800 px-4 sm:px-8">
          <nav className="flex gap-1 overflow-x-auto py-2" aria-label="Sections dossier RH">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className={`whitespace-nowrap rounded-lg border px-3 py-2 text-xs font-semibold transition sm:text-sm ${badgeClass(activeTab === t.id)}`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Body scroll */}
        <main className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-8 sm:py-6">
          {boot ? (
            <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 text-slate-400">
              <Loader2 className="h-10 w-10 animate-spin" />
              <p className="text-sm">Chargement de l’agent terrain…</p>
            </div>
          ) : (
            <>
              {activeTab === "identite" && (
                <section className="space-y-5">
                  {/* TODO: brancher sur Person / identity service — champs RH en lecture dérivée ou override contrôlé */}
                  <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-5">
                    <h3 className="text-sm font-semibold text-violet-200">Identité</h3>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500">
                      Source de vérité future : entité Person ; ici saisie ou préremplissage depuis le
                      profil terrain sans dupliquer la logique métier à long terme.
                    </p>
                    <div className="mt-5 grid gap-4 md:grid-cols-2">
                      <label className="block text-sm">
                        <span className="text-slate-400">Nom</span>
                        <input
                          className={inputClass}
                          value={nom}
                          onChange={(e) => setNom(e.target.value)}
                        />
                      </label>
                      <label className="block text-sm">
                        <span className="text-slate-400">Prénom</span>
                        <input
                          className={inputClass}
                          value={prenom}
                          onChange={(e) => setPrenom(e.target.value)}
                        />
                      </label>
                    </div>
                    <div className="mt-4 grid gap-4 md:grid-cols-2">
                      <label className="block text-sm">
                        <span className="text-slate-400">Téléphone</span>
                        <input
                          className={inputClass}
                          value={telephone}
                          onChange={(e) => setTelephone(e.target.value)}
                        />
                      </label>
                      <label className="block text-sm">
                        <span className="text-slate-400">Email</span>
                        <input
                          type="email"
                          className={inputClass}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </label>
                    </div>
                  </div>
                </section>
              )}

              {activeTab === "contrat" && (
                <section className="rounded-2xl border border-slate-800 bg-slate-950/40 p-5">
                  <h3 className="text-sm font-semibold text-violet-200">Contrat RH</h3>
                  <div className="mt-5 space-y-4">
                    <label className="block text-sm">
                      <span className="text-slate-400">Matricule RH (optionnel)</span>
                      <input
                        className={inputClass}
                        value={matriculeRH}
                        onChange={(e) => setMatriculeRH(e.target.value)}
                        placeholder="Généré automatiquement si vide"
                      />
                    </label>
                    <div className="grid gap-4 md:grid-cols-2">
                      <label className="block text-sm">
                        <span className="text-slate-400">Statut RH</span>
                        <select
                          className={inputClass}
                          value={statutRH}
                          onChange={(e) => setStatutRH(e.target.value)}
                        >
                          {STATUTS.map((s) => (
                            <option key={s} value={s}>
                              {s.replace(/_/g, " ")}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="block text-sm">
                        <span className="text-slate-400">Type de contrat</span>
                        <input
                          className={inputClass}
                          value={typeContrat}
                          onChange={(e) => setTypeContrat(e.target.value)}
                          placeholder="CDI, CDD…"
                        />
                      </label>
                    </div>
                    <label className="block text-sm">
                      <span className="text-slate-400">Poste</span>
                      <input
                        className={inputClass}
                        value={posteRH}
                        onChange={(e) => setPosteRH(e.target.value)}
                      />
                    </label>
                    <div className="grid gap-4 md:grid-cols-2">
                      <label className="block text-sm">
                        <span className="text-slate-400">Date début</span>
                        <input
                          type="date"
                          className={inputClass}
                          value={dateEmbauche}
                          onChange={(e) => setDateEmbauche(e.target.value)}
                        />
                      </label>
                      <label className="block text-sm">
                        <span className="text-slate-400">Date fin (optionnel)</span>
                        <input
                          type="date"
                          className={inputClass}
                          value={dateFinContrat}
                          onChange={(e) => setDateFinContrat(e.target.value)}
                        />
                      </label>
                    </div>
                  </div>
                </section>
              )}

              {activeTab === "paie" && (
                <section className="rounded-2xl border border-slate-800 bg-slate-950/40 p-5">
                  <h3 className="text-sm font-semibold text-violet-200">Paie</h3>
                  <div className="mt-5 grid gap-4 md:grid-cols-2">
                    <label className="block text-sm">
                      <span className="text-slate-400">Salaire mensuel</span>
                      <input
                        className={inputClass}
                        value={salaireMensuel}
                        onChange={(e) => setSalaireMensuel(e.target.value)}
                        inputMode="decimal"
                      />
                    </label>
                    <label className="block text-sm">
                      <span className="text-slate-400">Mode de paiement</span>
                      <input
                        className={inputClass}
                        value={modePaiement}
                        onChange={(e) => setModePaiement(e.target.value)}
                        placeholder="VIREMENT, MOBILE_MONEY…"
                      />
                    </label>
                  </div>
                  <label className="mt-4 block text-sm">
                    <span className="text-slate-400">Compte / RIB / mobile money</span>
                    <input
                      className={inputClass}
                      value={comptePaiement}
                      onChange={(e) => setComptePaiement(e.target.value)}
                    />
                  </label>
                </section>
              )}

              {activeTab === "documents" && (
                <section className="rounded-2xl border border-dashed border-slate-600 bg-slate-950/30 p-6">
                  <div className="flex items-start gap-3">
                    <HardHat className="mt-0.5 h-5 w-5 shrink-0 text-slate-500" />
                    <div>
                      <h3 className="text-sm font-semibold text-slate-300">Documents</h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-500">
                        Placeholder lot suivant : contrat signé, pièce d’identité, attestations. Les
                        pièces rattachées au profil terrain restent sur la fiche agent jusqu’à
                        centralisation documentaire RH.
                      </p>
                    </div>
                  </div>
                </section>
              )}

              {activeTab === "liaison" && (
                <section className="space-y-4">
                  {agentPref && terrainAgent ? (
                    <div className="rounded-2xl border border-teal-500/30 bg-teal-950/20 p-5 text-sm text-teal-50">
                      <p className="font-semibold text-teal-100">Liaison active</p>
                      <p className="mt-2 leading-relaxed text-teal-100/85">
                        Ce dossier RH sera enregistré avec{" "}
                        <strong className="text-white">agentTerrainId = {agentPref}</strong>. Les
                        champs identité peuvent rester alignés sur la fiche terrain ou être adaptés
                        pour l’administratif.
                      </p>
                      <Link
                        to={`/admin/agents/${terrainAgent.id}`}
                        className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-teal-300 underline-offset-4 hover:underline"
                      >
                        Ouvrir la fiche agent terrain
                      </Link>
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-slate-700 bg-slate-950/40 p-5 text-sm text-slate-400">
                      <p className="font-medium text-slate-200">Dossier RH autonome</p>
                      <p className="mt-2 leading-relaxed">
                        Aucun agent terrain fourni dans l’URL. Vous pourrez lier un agent plus tard
                        depuis la fiche employé RH (fonction « Liaison terrain »).
                      </p>
                    </div>
                  )}
                </section>
              )}
            </>
          )}
        </main>

        <footer className="relative z-10 shrink-0 border-t border-slate-800 bg-slate-950/95 p-4 backdrop-blur-sm sm:px-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={onClose}
              className="min-h-11 w-full rounded-xl border border-slate-600 bg-slate-800/50 px-4 py-2.5 text-sm font-medium text-slate-200 hover:bg-slate-800 sm:w-auto"
            >
              Annuler
            </button>
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:justify-end sm:gap-2">
              <button
                type="button"
                disabled={loading || boot}
                onClick={() => void submit("list")}
                className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-violet-500/50 bg-violet-950/50 px-5 py-2.5 text-sm font-semibold text-violet-100 hover:bg-violet-950/80 disabled:opacity-50 sm:w-auto"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {primaryCreateLabel}
              </button>
              <button
                type="button"
                disabled={loading || boot}
                onClick={() => void submit("detail")}
                className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-500 disabled:opacity-50 sm:w-auto sm:min-w-[14rem]"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {secondaryCreateLabel}
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
